import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Inject, Optional, ViewEncapsulation } from '@angular/core';
import { NgxMatCalendar, NgxMatDateAdapter, NGX_MAT_DATE_FORMATS, NgxMatDateFormats } from '@angular-material-components/datetime-picker';

/**
 * Header custom pro `<ngx-mat-calendar>` (dentro do `ngx-mat-datetime-picker`).
 * Substitui o `NgxMatCalendarHeader` default da lib, que faz `.toLocaleUpperCase()`
 * HARDCODED em cima do rótulo do mês/ano — impedindo qualquer format nosso de
 * aparecer no chair UI (a lib retornaria "JUN. DE 2026").
 *
 * Aqui devolvemos o resultado do format() SEM uppercase, respeitando o
 * `monthYearLabel` de `NGX_MAT_DATE_FORMATS` (definido no `app.module.ts`).
 *
 * Botões ‹ › (previous/next) e o click no rótulo (troca de view) delegam
 * pros métodos padrão do NgxMatCalendar.
 */
@Component({
  selector: 'app-jems-calendar-header',
  template: `
    <div class="mat-calendar-header">
      <div class="mat-calendar-controls">
        <button mat-button type="button" class="mat-calendar-period-button"
                (click)="currentPeriodClicked()"
                cdkAriaLive="polite">
          {{ periodButtonText }}
          <div class="mat-calendar-arrow"
               [class.mat-calendar-invert]="calendar.currentView !== 'month'"></div>
        </button>
        <div class="mat-calendar-spacer"></div>
        <button mat-icon-button type="button" class="mat-calendar-previous-button"
                [disabled]="!previousEnabled"
                (click)="previousClicked()">
        </button>
        <button mat-icon-button type="button" class="mat-calendar-next-button"
                [disabled]="!nextEnabled"
                (click)="nextClicked()">
        </button>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class JemsCalendarHeaderComponent {
  private static readonly YEARS_PER_PAGE = 24;

  constructor(
    @Inject(forwardRef(() => NgxMatCalendar)) public calendar: NgxMatCalendar<any>,
    @Optional() private dateAdapter: NgxMatDateAdapter<any>,
    @Optional() @Inject(NGX_MAT_DATE_FORMATS) private dateFormats: NgxMatDateFormats,
    cdr: ChangeDetectorRef,
  ) {
    calendar.stateChanges.subscribe(() => cdr.markForCheck());
  }

  get periodButtonText(): string {
    if (this.calendar.currentView === 'month') {
      // A lib original chama `.toLocaleUpperCase()` no resultado. Aqui só
      // capitalizamos a 1ª letra (pt-BR "jun. de 2026" → "Jun. de 2026").
      const raw = this.dateAdapter.format(this.calendar.activeDate, this.dateFormats.display.monthYearLabel);
      return raw.charAt(0).toLocaleUpperCase() + raw.slice(1);
    }
    if (this.calendar.currentView === 'year') {
      return this.dateAdapter.getYearName(this.calendar.activeDate);
    }
    const yearsPerPage = JemsCalendarHeaderComponent.YEARS_PER_PAGE;
    const activeYear = this.dateAdapter.getYear(this.calendar.activeDate);
    const firstYearInView = activeYear - (activeYear % yearsPerPage);
    const lastYearInView = firstYearInView + yearsPerPage - 1;
    return `${this.dateAdapter.getYearName(this.dateAdapter.createDate(firstYearInView, 0, 1))} – ${this.dateAdapter.getYearName(this.dateAdapter.createDate(lastYearInView, 0, 1))}`;
  }

  /** Clique no rótulo do período: alterna entre "mês" e "multi-year"
   *  (comportamento default do MatCalendar/NgxMatCalendar). */
  currentPeriodClicked(): void {
    this.calendar.currentView = this.calendar.currentView === 'month' ? 'multi-year' : 'month';
  }

  /** Navega pra o período anterior (mês / ano / bloco de 24 anos). */
  previousClicked(): void {
    const cv = this.calendar.currentView;
    this.calendar.activeDate = cv === 'month'
      ? this.dateAdapter.addCalendarMonths(this.calendar.activeDate, -1)
      : this.dateAdapter.addCalendarYears(this.calendar.activeDate, cv === 'year' ? -1 : -JemsCalendarHeaderComponent.YEARS_PER_PAGE);
  }

  /** Navega pra o próximo período. */
  nextClicked(): void {
    const cv = this.calendar.currentView;
    this.calendar.activeDate = cv === 'month'
      ? this.dateAdapter.addCalendarMonths(this.calendar.activeDate, 1)
      : this.dateAdapter.addCalendarYears(this.calendar.activeDate, cv === 'year' ? 1 : JemsCalendarHeaderComponent.YEARS_PER_PAGE);
  }

  get previousEnabled(): boolean {
    if (!this.calendar.minDate) { return true; }
    return !this.isSameView(this.calendar.activeDate, this.calendar.minDate);
  }

  get nextEnabled(): boolean {
    return !this.calendar.maxDate || !this.isSameView(this.calendar.activeDate, this.calendar.maxDate);
  }

  private isSameView(d1: any, d2: any): boolean {
    if (this.calendar.currentView === 'month') {
      return this.dateAdapter.getYear(d1) === this.dateAdapter.getYear(d2)
          && this.dateAdapter.getMonth(d1) === this.dateAdapter.getMonth(d2);
    }
    if (this.calendar.currentView === 'year') {
      return this.dateAdapter.getYear(d1) === this.dateAdapter.getYear(d2);
    }
    const yearsPerPage = JemsCalendarHeaderComponent.YEARS_PER_PAGE;
    return Math.floor(this.dateAdapter.getYear(d1) / yearsPerPage)
        === Math.floor(this.dateAdapter.getYear(d2) / yearsPerPage);
  }
}
